import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';
import ThemeToggle from '@/components/feature/ThemeToggle';
import BranchSwitcher from '@/components/feature/BranchSwitcher';
import AccountMenu from '@/components/feature/AccountMenu';
import { useActivityLogRetention } from '@/hooks/useActivityLogRetention';

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const ADMIN_NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: 'ri-dashboard-line' },
  { to: '/pos', label: 'POS / Facturar', icon: 'ri-shopping-cart-2-line' },
  { to: '/products', label: 'Productos', icon: 'ri-box-3-line' },
  { to: '/inventory', label: 'Inventario', icon: 'ri-archive-line' },
  { to: '/sales', label: 'Ventas', icon: 'ri-receipt-line' },
  { to: '/invoices', label: 'Facturas', icon: 'ri-file-list-3-line' },
  { to: '/cash', label: 'Cuadre de caja', icon: 'ri-wallet-3-line' },
  { to: '/cash/supervision', label: 'Cuadres', icon: 'ri-folder-shield-2-line' },
  { to: '/clients', label: 'Clientes', icon: 'ri-user-3-line' },
  { to: '/catalogo', label: 'Catálogo', icon: 'ri-store-3-line' },
  { to: '/branches', label: 'Sucursales', icon: 'ri-building-2-line' },
  { to: '/users', label: 'Usuarios', icon: 'ri-team-line' },
  { to: '/discounts', label: 'Descuentos', icon: 'ri-percent-line' },
  { to: '/offers', label: 'Ofertas', icon: 'ri-price-tag-3-line' },
  { to: '/wholesale', label: 'Mayoristas', icon: 'ri-user-star-line' },
  { to: '/reports', label: 'Reportes', icon: 'ri-bar-chart-2-line' },
  { to: '/reports/ganancias', label: 'Ganancias', icon: 'ri-funds-line' },
  { to: '/fiscal', label: 'Facturación e-CF', icon: 'ri-file-shield-2-line' },
  { to: '/settings', label: 'Configuración', icon: 'ri-settings-3-line' },
  { to: '/settings/logs', label: 'Seguridad', icon: 'ri-shield-check-line' },
  { to: '/settings/diagnostics', label: 'Diagnóstico', icon: 'ri-pulse-line' },
];

const CASHIER_NAV: NavItem[] = [
  { to: '/pos', label: 'POS / Facturar', icon: 'ri-shopping-cart-2-line' },
  { to: '/sales', label: 'Ventas', icon: 'ri-receipt-line' },
  { to: '/cash', label: 'Cuadre de caja', icon: 'ri-wallet-3-line' },
  { to: '/reports/ganancias', label: 'Ganancias', icon: 'ri-funds-line' },
  { to: '/clients', label: 'Clientes', icon: 'ri-user-3-line' },
];

const MOBILE_BOTTOM_ADMIN = [
  { to: '/', label: 'Inicio', icon: 'ri-dashboard-line' },
  { to: '/pos', label: 'POS', icon: 'ri-shopping-cart-2-line' },
  { to: '/products', label: 'Productos', icon: 'ri-box-3-line' },
  { to: '/inventory', label: 'Inventario', icon: 'ri-archive-line' },
];

const MOBILE_BOTTOM_CASHIER = [
  { to: '/pos', label: 'POS', icon: 'ri-shopping-cart-2-line' },
  { to: '/sales', label: 'Ventas', icon: 'ri-receipt-line' },
  { to: '/clients', label: 'Clientes', icon: 'ri-user-3-line' },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { profile, signOut, isAdmin } = useAuth();
  const { branches, activeBranchId, activeBranch, setActiveBranchId } = useBranch();
  // Limpieza automática mensual de los registros de éxito y aviso (nunca de los errores).
  useActivityLogRetention(isAdmin);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const navItems = isAdmin ? ADMIN_NAV : CASHIER_NAV;
  const bottomItems = isAdmin ? MOBILE_BOTTOM_ADMIN : MOBILE_BOTTOM_CASHIER;

  // El cajero nunca debe ver "Inicio": si aterriza en la raíz, lo mandamos al POS.
  useEffect(() => {
    if (!isAdmin && location.pathname === '/') {
      navigate('/pos', { replace: true });
    }
  }, [isAdmin, location.pathname, navigate]);

  const fullName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    profile?.email ||
    'Usuario';
  const roleLabel = isAdmin ? 'Administrador' : 'Cajero';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  // Modo pantalla completa de facturación (POS): sin menú lateral.
  const isPosRoute = location.pathname === '/pos' || location.pathname.startsWith('/pos/');

  // Botones de navegación reacomodados para la barra superior del POS.
  const posNav: NavItem[] = isAdmin
    ? [
        { to: '/', label: 'Inicio', icon: 'ri-dashboard-line' },
        { to: '/pos', label: 'Facturar', icon: 'ri-shopping-cart-2-line' },
        { to: '/products', label: 'Productos', icon: 'ri-box-3-line' },
        { to: '/inventory', label: 'Inventario', icon: 'ri-archive-line' },
        { to: '/sales', label: 'Ventas', icon: 'ri-receipt-line' },
        { to: '/cash', label: 'Cuadre', icon: 'ri-wallet-3-line' },
        { to: '/clients', label: 'Clientes', icon: 'ri-user-3-line' },
      ]
    : [
        { to: '/pos', label: 'Facturar', icon: 'ri-shopping-cart-2-line' },
        { to: '/sales', label: 'Ventas', icon: 'ri-receipt-line' },
        { to: '/cash', label: 'Cuadre', icon: 'ri-wallet-3-line' },
        { to: '/clients', label: 'Clientes', icon: 'ri-user-3-line' },
      ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Sidebar escritorio (oculto en la pantalla completa del POS) */}
      {!isPosRoute && (
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-background-200 bg-background-50 z-40">
        <div className="flex items-center gap-3 px-5 h-16 border-b border-background-200">
          {activeBranch?.logo_url ? (
            <img
              src={activeBranch.logo_url}
              alt="Logo"
              className="w-12 h-12 rounded-lg object-contain bg-background-100 shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 shrink-0">
              <i className="ri-store-2-line text-2xl" />
            </div>
          )}
          <span className="font-heading font-bold text-foreground-950 truncate">
            {activeBranch?.name ?? 'Mi Tienda'}
          </span>
        </div>

        {/* Selector de sucursal */}
        <div className="px-4 py-3 border-b border-background-200">
          {isAdmin ? (
            <>
              <label className="text-xs font-medium text-foreground-500 flex items-center gap-1.5">
                <i className="ri-building-2-line" /> Sucursal activa
              </label>
              <select
                value={activeBranchId ?? ''}
                onChange={(e) => setActiveBranchId(e.target.value || null)}
                className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
              >
                <option value="">Todas las sucursales</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <i className="ri-building-2-line text-foreground-500" />
              <span className="text-foreground-700 truncate">
                {activeBranch?.name ?? 'Sin sucursal asignada'}
              </span>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium whitespace-nowrap transition ${
                isActive(item.to)
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              <i className={`${item.icon} text-lg`} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="border-t border-background-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center font-semibold shrink-0">
              {fullName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-950 truncate">{fullName}</p>
              <p className="text-xs text-foreground-500">{roleLabel}</p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-r-line" /> Cerrar sesión
          </button>
          <div className="mt-2">
            <ThemeToggle direction="up" showLabel />
          </div>
        </div>
      </aside>
      )}

      {/* Barra superior del POS (pantalla completa): navegación a la izquierda, sucursal/cuenta a la derecha */}
      {isPosRoute && (
        <header className="hidden lg:flex sticky top-0 z-40 items-center justify-between gap-4 border-b border-background-200 bg-background-50/90 backdrop-blur-md px-4 h-14">
          <nav className="flex items-center gap-1 min-w-0 overflow-x-auto">
            {posNav.map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition ${
                    active
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${item.icon} text-base`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <BranchSwitcher />
            <AccountMenu onSignOut={handleSignOut} />
          </div>
        </header>
      )}

      {/* Header móvil */}
      <header className="lg:hidden sticky top-0 z-40 bg-background-50/85 backdrop-blur-md border-b border-background-200/70">
        <div className="px-4 h-14 flex items-center justify-between gap-3">
          <BranchSwitcher />
          <AccountMenu onSignOut={handleSignOut} />
        </div>

        {menuOpen && (
          <nav className="px-3 pb-3 border-t border-background-200 bg-background-50">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition ${
                  isActive(item.to)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={`${item.icon} text-lg`} /> {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Contenido */}
      <main className={isPosRoute ? '' : 'lg:pl-64'}>
        <div
          className={
            isPosRoute
              ? 'px-4 md:px-6 py-4 md:py-5 pb-24 lg:pb-6'
              : 'px-4 md:px-6 py-6 md:py-8 pb-32 lg:pb-8'
          }
        >
          {children}
        </div>
      </main>

      {/* Menú inferior móvil */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50 border-t border-background-200">
        <div className="flex items-stretch justify-around">
          {bottomItems.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex-1 flex flex-col items-center gap-1 py-1.5 text-[11px] font-medium transition ${
                  active ? 'text-primary-600 dark:text-primary-300' : 'text-foreground-500'
                }`}
              >
                <span
                  className={`flex items-center justify-center w-12 h-7 rounded-full transition ${
                    active ? 'bg-primary-50 dark:bg-primary-100/50' : ''
                  }`}
                >
                  <i className={`${item.icon} text-xl`} />
                </span>
                {item.label}
              </Link>
            );
          })}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex-1 flex flex-col items-center gap-1 py-1.5 text-[11px] font-medium text-foreground-500 cursor-pointer"
          >
            <span className="flex items-center justify-center w-12 h-7 rounded-full">
              <i className="ri-more-2-line text-xl" />
            </span>
            Más
          </button>
        </div>
      </nav>

      {/* Menú "Más" móvil */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setMoreOpen(false)}>
          <div
            className="w-full bg-background-50 rounded-t-2xl p-5 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-foreground-950">Menú</h3>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className="flex flex-col items-center gap-2 rounded-lg border border-background-200 p-3 text-center text-xs font-medium text-foreground-700 hover:bg-background-100 transition"
                >
                  <i className={`${item.icon} text-2xl text-primary-700`} />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}